import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DietaryPreferences from "./DietaryPreferences";
import CookingTimePreferences from "./CookingTimePreferences";
import CuisinePreferences from "./CuisinePreferences";
import PlanSettings from "./PlanSettings";
import PreviewPanel from "./PreviewPanel";
import { ChevronLeft, ChevronRight, Eye } from "lucide-react";
import { translations } from "@/lib/translations";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

interface PreferenceWizardProps {
  onComplete?: (preferences: WizardData) => void;
  initialData?: Partial<WizardData>;
}

interface WizardData {
  dietary: {
    vegan: boolean;
    vegetarian: boolean;
    glutenFree: boolean;
    none: boolean;
  };
  cookingTime: number;
  cuisines: string[];
  includedIngredients: string[];
  excludedIngredients: string[];
  duration: number;
  servings: number;
}

const defaultData: WizardData = {
  dietary: {
    vegan: false,
    vegetarian: false,
    glutenFree: false,
    none: true,
  },
  cookingTime: 45,
  cuisines: ["Italian", "Japanese"],
  includedIngredients: [],
  excludedIngredients: [],
  duration: 7,
  servings: 4,
};

export default function PreferenceWizard({
  onComplete = () => {},
  initialData,
}: PreferenceWizardProps) {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [wizardData, setWizardData] = React.useState<WizardData>({
    ...defaultData,
    ...initialData,
  });

  const handleDietaryChange = (key: string, value: boolean) => {
    const newDietary = {
      vegan: false,
      vegetarian: false,
      glutenFree: false,
      none: false,
    };

    if (value) {
      newDietary[key as keyof typeof newDietary] = true;
    } else {
      newDietary.none = true;
    }

    setWizardData({
      ...wizardData,
      dietary: newDietary,
    });
  };

  const steps = [
    {
      title: translations.mealPlanner.steps.dietaryRestrictions,
      component: (
        <DietaryPreferences
          preferences={wizardData.dietary}
          onPreferenceChange={handleDietaryChange}
        />
      ),
    },
    {
      title: translations.mealPlanner.steps.cookingTime,
      component: (
        <CookingTimePreferences
          initialTime={wizardData.cookingTime}
          onTimeChange={(time) =>
            setWizardData({ ...wizardData, cookingTime: time })
          }
        />
      ),
    },
    {
      title: translations.mealPlanner.steps.cuisineAndIngredients,
      component: (
        <CuisinePreferences
          selectedCuisines={wizardData.cuisines}
          includedIngredients={wizardData.includedIngredients}
          excludedIngredients={wizardData.excludedIngredients}
          onCuisineChange={(cuisines) =>
            setWizardData({ ...wizardData, cuisines })
          }
          onIncludeIngredient={(ingredient) =>
            setWizardData({
              ...wizardData,
              includedIngredients: [
                ...wizardData.includedIngredients,
                ingredient,
              ],
            })
          }
          onExcludeIngredient={(ingredient) =>
            setWizardData({
              ...wizardData,
              excludedIngredients: [
                ...wizardData.excludedIngredients,
                ingredient,
              ],
            })
          }
          onRemoveIncludedIngredient={(ingredient) =>
            setWizardData({
              ...wizardData,
              includedIngredients: wizardData.includedIngredients.filter(
                (i) => i !== ingredient,
              ),
            })
          }
          onRemoveExcludedIngredient={(ingredient) =>
            setWizardData({
              ...wizardData,
              excludedIngredients: wizardData.excludedIngredients.filter(
                (i) => i !== ingredient,
              ),
            })
          }
        />
      ),
    },
    {
      title: translations.mealPlanner.steps.planSettings,
      component: (
        <PlanSettings
          duration={wizardData.duration}
          servings={wizardData.servings}
          onDurationChange={(duration) =>
            setWizardData({ ...wizardData, duration })
          }
          onServingsChange={(servings) =>
            setWizardData({ ...wizardData, servings })
          }
        />
      ),
    },
  ];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete(wizardData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const PreviewButton = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" className="md:hidden">
          <Eye className="w-4 h-4 ml-2" />
          {translations.common.preview}
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-full sm:w-[400px] p-0">
        <PreviewPanel preferences={wizardData} />
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="w-full min-h-[700px] bg-gray-50 p-4 md:p-6 flex flex-col md:flex-row gap-6">
      <div className="flex-1">
        <Card className="p-4 md:p-6 bg-white">
          {/* Progress Indicator */}
          <div className="mb-8 overflow-x-auto">
            <div className="flex justify-between items-center mb-4 min-w-[600px] md:min-w-0">
              {steps.map((step, index) => (
                <div key={step.title} className="flex items-center flex-1">
                  <div
                    className={`flex flex-col items-center ${index <= currentStep ? "text-primary" : "text-gray-400"}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${index <= currentStep ? "bg-primary text-white" : "bg-gray-200"}`}
                    >
                      {index + 1}
                    </div>
                    <span className="text-sm font-medium text-center">
                      {step.title}
                    </span>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`flex-1 h-1 mx-4 ${index < currentStep ? "bg-primary" : "bg-gray-200"}`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Preview Button for Mobile */}
          <div className="mb-4 md:hidden">
            <PreviewButton />
          </div>

          {/* Step Content */}
          <div className="mb-8">{steps[currentStep].component}</div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={handleBack}
              className="rtl-button-icon"
              disabled={currentStep === 0}
            >
              <ChevronRight className="w-4 h-4 ml-2" />
              {translations.common.back}
            </Button>
            <Button onClick={handleNext}>
              {currentStep === steps.length - 1 ? (
                <span className="rtl-button-icon">
                  {translations.common.complete}
                </span>
              ) : (
                translations.common.next
              )}
              {currentStep < steps.length - 1 && (
                <ChevronLeft className="w-4 h-4 mr-2" />
              )}
            </Button>
          </div>
        </Card>
      </div>

      {/* Preview Panel - Desktop Only */}
      <div className="hidden md:block w-[400px]">
        <PreviewPanel preferences={wizardData} />
      </div>
    </div>
  );
}
